import { useEffect } from "react";
import SEOMeta from "@/components/SEOMeta";
import { useParams, Link, useNavigate } from "react-router-dom";
import { usePublicBlogPostBySlug } from "@/hooks/useBlogPosts";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { readingTimeLabel } from "@/lib/readingTime";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: post, isLoading: loading, isError } = usePublicBlogPostBySlug(slug);

  // Não publicado, inexistente ou erro: volta para a lista (comportamento anterior).
  useEffect(() => {
    if (!loading && (isError || post === null)) navigate("/blog");
  }, [loading, isError, post, navigate]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    return format(new Date(dateString), "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavbarNew />
        <main>
          <div className="mx-auto max-w-[720px] animate-pulse px-8 pb-[90px] pt-14 max-[900px]:px-5">
            <div className="mb-6 h-4 w-1/4 rounded bg-muted" />
            <div className="mb-4 h-3 w-1/3 rounded bg-muted" />
            <div className="mb-4 h-10 w-3/4 rounded bg-muted" />
            <div className="mb-8 h-5 w-full rounded bg-muted" />
            <div className="mb-8 aspect-video rounded-2xl bg-muted" />
            <div className="space-y-4">
              <div className="h-4 w-full rounded bg-muted" />
              <div className="h-4 w-5/6 rounded bg-muted" />
              <div className="h-4 w-4/6 rounded bg-muted" />
            </div>
          </div>
        </main>
        <FooterNew />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title={post.title}
        description={post.excerpt || `Leia o artigo "${post.title}" no blog de Eliveuton Dev.`}
        canonicalUrl={`/blog/${post.slug}`}
        ogImage={post.cover_image_url || undefined}
        ogType="article"
        publishedTime={post.published_at || post.created_at}
      />
      <NavbarNew />

      <main>
        <article className="mx-auto max-w-[720px] px-8 pb-[90px] pt-14 max-[900px]:px-5">
          <Link
            to="/blog"
            className="inline-flex items-center gap-[7px] text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-[15px] w-[15px]" />
            Voltar ao blog
          </Link>

          <div className="mt-[26px] flex flex-wrap items-center gap-2.5 text-xs text-muted-foreground">
            {post.blog_categories?.name && (
              <>
                <span className="font-bold text-primary">{post.blog_categories.name}</span>
                <span>·</span>
              </>
            )}
            <span>{formatDate(post.published_at || post.created_at)}</span>
            <span>·</span>
            <span>{readingTimeLabel(post.content, "min de leitura")}</span>
          </div>

          <h1 className="mt-[14px] text-[40px] font-bold leading-[1.12] tracking-[-0.04em] max-[900px]:text-[38px]">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mt-[18px] text-lg leading-[1.65] text-muted-foreground">{post.excerpt}</p>
          )}

          {post.cover_image_url && (
            <div className="mt-[34px] aspect-video overflow-hidden rounded-2xl border border-border">
              <img
                src={post.cover_image_url}
                alt={post.title}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <div className="prose prose-lg mt-8 max-w-none dark:prose-invert [&_h2]:text-[23px] [&_h2]:font-bold [&_h2]:tracking-[-0.025em] [&_p]:text-[16.5px] [&_p]:leading-[1.8]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          <div className="mt-11 rounded-[20px] border border-primary bg-accent px-[30px] py-7">
            <div className="text-[17px] font-bold tracking-[-0.02em]">
              Quer saber se dá pra fazer isso no seu caso?
            </div>
            <p className="mb-[18px] mt-[9px] text-[14.5px] leading-[1.6] text-muted-foreground">
              30 minutos olhando seu processo. Eu digo o que vale automatizar primeiro — mesmo que
              você não feche nada.
            </p>
            <Link
              to="/contato"
              className="inline-flex h-[46px] items-center rounded-full bg-primary px-[22px] text-[14px] font-bold text-primary-foreground transition hover:brightness-95"
            >
              Agendar diagnóstico gratuito
            </Link>
          </div>
        </article>
      </main>

      <FooterNew />
    </div>
  );
};

export default BlogPost;
